import {useCallback, useState} from 'react'
import {queryForRepos} from '@github-ui/copilot-chat/components/TaskChatInputToolbar'
import type {AgentDropdownRepository} from '@github-ui/copilot-chat/utils/copilot-chat-types'
import {ReposSelector, isNoRepoSelection} from '@github-ui/repos-selector'
import {NO_REPO_FILTER} from '../../hooks/use-repo-tasks-filter'

interface RepoFilterPickerProps {
  /** Current filter: undefined = all repos, "none" = no repo, NWO = specific repo */
  repoFilter: string | undefined
  /** Callback when filter changes */
  onFilterChange: (repo: AgentDropdownRepository | undefined) => void
}

/**
 * Repository filter picker for the agents page.
 * Wraps ReposSelector with "All repositories" and "No repository" options.
 */
export function RepoFilterPicker({repoFilter, onFilterChange}: RepoFilterPickerProps) {
  const [selectedRepo, setSelectedRepo] = useState<AgentDropdownRepository | undefined>(() => {
    // Hydrate from URL on initial render so picker reflects the active filter after hard refresh
    if (repoFilter && repoFilter !== NO_REPO_FILTER) {
      const [owner, name] = repoFilter.split('/', 2)
      if (owner && name) {
        return {
          name: repoFilter,
          nameOnly: name,
          nameWithOwner: repoFilter,
          owner,
          enabled: true,
          defaultBranch: 'main',
          private: false,
          isEmpty: false,
        }
      }
    }
    return undefined
  })
  const isNoRepo = repoFilter === NO_REPO_FILTER

  const repositoryLoader = useCallback((query: string, defaultOwner?: string) => queryForRepos(query, defaultOwner), [])

  const handleSelect = useCallback(
    (repo: AgentDropdownRepository | undefined) => {
      if (repo && !isNoRepoSelection(repo)) {
        setSelectedRepo(repo)
      } else {
        setSelectedRepo(undefined)
      }
      onFilterChange(repo)
    },
    [onFilterChange],
  )

  return (
    <ReposSelector<AgentDropdownRepository>
      repositoryLoader={repositoryLoader}
      selectionVariant="single"
      selectAllOption
      noRepoOption
      onSelect={handleSelect}
      currentSelection={isNoRepo ? undefined : selectedRepo}
      width="medium"
      buttonText={isNoRepo ? 'No repository' : undefined}
      additionalButtonProps={{
        variant: 'invisible',
        'aria-label': `Filter by repository: ${isNoRepo ? 'No repository' : selectedRepo?.nameWithOwner || 'All repositories'}`,
      }}
    />
  )
}
